import { useAuthStore } from '@/store/authStore';
import { TokenMonitoring, TokenAdmin, TenantId } from '@/store/util';
import axios, { type AxiosError } from 'axios';
import { Telemetry } from '@/lib/telemetry';

function trackApiError(error: AxiosError) {
  const status = error.response?.status;
  const method = error.config?.method?.toUpperCase() ?? 'UNKNOWN';
  const url = error.config?.url ?? 'unknown';

  // Always add a breadcrumb for any API error (visible in Sentry error timelines)
  Telemetry.addBreadcrumb('api', `${method} ${url} → ${status}`, { status, url, method }, status && status >= 500 ? 'error' : 'warning');

  // Capture 5xx errors as alertable Sentry events
  if (status && status >= 500) {
    Telemetry.captureError(error, {
      tags: { api_status: status, api_url: url },
      contexts: {
        response: {
          status,
          data: error.response?.data as Record<string, unknown>,
        },
      },
    });
  }
}

export const monitoring_api: string =
  import.meta.env.VITE_MONITORING_API || 'https://api.monitoring.test.watchful.cloud';
export const admin_api: string =
  import.meta.env.VITE_ADMIN_API || 'https://api.admin.test.watchful.cloud';

// Create an axios instance with default configuration
const axiosClient = axios.create({
  baseURL: monitoring_api, // Replace with your API's base URL
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add an interceptor to include the bearer token in every request
axiosClient.interceptors.request.use((config) => {
  const token = TokenMonitoring.get()

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle token expiry
axiosClient.interceptors.response.use(
  (response) => response,
  (error) => {
    trackApiError(error);

    if (error.response?.status === 401) {
      // Token has expired or is invalid
      const authStore = useAuthStore.getState();

      // If already logging out, don't trigger another logout (prevents redirect loops
      // when endShift fails with 401 during logout)
      if (authStore.loggingOut) {
        return Promise.reject(error);
      }

      // If we were never authorized, we should be logged in as admin
      if (authStore.isAuthenticatedMonitor) {
        authStore.logout(true);
      }
    }
    return Promise.reject(error);
  }
);

export default axiosClient;

// Create a separate axios instance for customer authentication
export const axiosCustomerClient = axios.create({
  baseURL: monitoring_api,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add an interceptor to include the customer bearer token in every request
axiosCustomerClient.interceptors.request.use((config) => {
  const customerToken = localStorage.getItem('customerToken');
  if (customerToken) {
    config.headers['Authorization'] = `Bearer ${customerToken}`;
  }

  const customerTenantId = localStorage.getItem('customerTenantId');
  if (customerTenantId) {
    config.headers['X-Tenant-Id'] = customerTenantId;
  }

  return config;
});

// Add response interceptor to handle token expiry for customer
axiosCustomerClient.interceptors.response.use(
  (response) => response,
  (error) => {
    trackApiError(error);

    if (error.response?.status === 401) {
      // Customer token has expired or is invalid
      const authStore = useAuthStore.getState();

      // If already logging out, don't trigger another logout
      if (authStore.loggingOut) {
        return Promise.reject(error);
      }

      if (authStore.isCustomerUser) {
        // Logout and go to the alert-specific login page
        authStore.logout();
        window.location.href = '/alert-login';
      }
    }
    return Promise.reject(error);
  }
);

export const axiosClientAdmin = axios.create({
  baseURL: admin_api,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosClientAdmin.interceptors.request.use((config) => {
  const token = TokenAdmin.get()

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  const tenantId = TenantId.get()

  if (tenantId) {
    config.headers['X-Tenant-Id'] = tenantId;
  }

  return config;
});

// Add response interceptor to handle token expiry for admin
axiosClientAdmin.interceptors.response.use(
  (response) => response,
  (error) => {
    trackApiError(error);

    if (error.response?.status === 401) {
      const authStore = useAuthStore.getState();

      // If already logging out, don't trigger another logout
      if (authStore.loggingOut) {
        return Promise.reject(error);
      }

      // If we were never authorized, we should be logged in as monitoring
      if (authStore.isAuthenticatedAdmin) {
        authStore.logout(true);
      }
    }
    return Promise.reject(error);
  }
);

export function getPreferredClient() {
  const { isAuthenticatedMonitor, isAuthenticatedAdmin } = useAuthStore.getState();
  
  if (isAuthenticatedMonitor) {
    return axiosClient;
  }
  if (isAuthenticatedAdmin) {
    return axiosClientAdmin;
  }
  return axiosClient;
}
